import { useState } from 'react';
import { isEmptyObject } from '@tiptap/core';
import type { Layout } from '@mentimeter/http-clients';
import type { LineHeightsEnum } from '@mentimeter/ragnar-dsc';
import type { Responsive } from '@mentimeter/ragnar-styled';
import { Label } from '@mentimeter/slides-presentation-ui/title/label';
import { Description } from '@mentimeter/slides-presentation-ui/title/description';
import { DEFAULT_FONT_OFFSET_TO_TITLE_HEIGHT } from '@mentimeter/slides-presentation-ui/text/resizable-title/font-offset-to-title-size';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { FlexibleText } from '@mentimeter/slides-presentation-ui/text/flexible-text';
import { MarkdownText } from '@mentimeter/slides-presentation-ui/text/markdown-text';
import { TextMarkupRenderer } from '@mentimeter/tiptap/text-markup-renderer';
import { useTranslations } from 'next-intl';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { StaggerCss } from './StaggerCss';

function getBreakpoints(layout: Layout | undefined) {
  const layoutSize = parseInt(layout?.size ?? '25', 10) || 100;

  const factor = (() => {
    if (
      layout?.type === 'side-by-side' ||
      layout?.type === 'side-by-side-right'
    ) {
      return 1 / 2;
    }
    if (layout?.type === 'left' || layout?.type === 'right') {
      return 1 - layoutSize / 100;
    }
    return 1;
  })();
  const layoutOffset = (() => {
    if (
      layout?.type === 'side-by-side' ||
      layout?.type === 'side-by-side-right'
    ) {
      return -2;
    }
    if (layout?.type === 'left' || layout?.type === 'right') {
      return layoutSize >= 50 ? -3 : -2;
    }
    return 0;
  })();

  return [
    {
      count: 150 * factor,
      offset: -3 + layoutOffset,
    },
    {
      count: 100 * factor,
      offset: -2 + layoutOffset,
    },
    {
      count: 50 * factor,
      offset: -1 + layoutOffset,
    },
  ];
}

interface TitleSectionProps extends BoxT {
  question: string;
  questionStyled?: Record<string, unknown>;
  question_description?: string;
  title_meta?: string;
  placeholder?: string;
  enablePlaceholderAlignment?: boolean | undefined;
  layout?: Layout | undefined;
  textAlign?: 'left' | 'right' | 'center';
  enableAnimations?: boolean;
  lineHeight?: Responsive<LineHeightsEnum>;
  titleOffset?: number;
}

/**
 * @deprecated Legacy title component, use `TitleSection` in interactive slides and for the content ones build custom title section
 */
export function ContentSlideTitleSection({
  question,
  questionStyled,
  question_description: questionDescription,
  title_meta: titleMeta,
  layout,
  placeholder: propsPlaceholder,
  enablePlaceholderAlignment = false,
  textAlign = 'left',
  alignItems = 'flex-start',
  enableAnimations = false,
  lineHeight = 'heading',
  titleOffset,
  ...props
}: TitleSectionProps) {
  const t = useTranslations('creation.canvas');
  const placeholder = propsPlaceholder || t('ask_your_question_placeholder');
  const [shouldShowDescription, setShouldShowDescription] = useState(false);
  const {
    Utils: { fontOffset },
    useConfig,
    InteractiveCanvasComponents,
  } = usePresentationContext();

  const { enableAnimations: enableAnimationsFromSDK } = useConfig();
  const shouldAnimate = enableAnimations && enableAnimationsFromSDK;

  const offset =
    fontOffset({
      width: question.length,
      height: 1,
      breakpoints: getBreakpoints(layout),
    }) + (layout?.font_size_offset ?? 0);

  const titleMinHeight =
    DEFAULT_FONT_OFFSET_TO_TITLE_HEIGHT[layout?.font_size_offset ?? 0];

  return (
    <Box
      width="100%"
      mb={3}
      extend={() => ({
        minHeight: `${titleMinHeight}px`,
        '& .hover': {
          opacity: 0,
        },
        '&:hover .hover': {
          opacity: 1,
        },
      })}
      position="relative"
      onMouseEnter={() => setShouldShowDescription(true)}
      onMouseLeave={() => setShouldShowDescription(false)}
      {...props}
    >
      <StaggerCss index={1} enableAnimations={shouldAnimate} type="brand">
        <Box width="100%" alignItems={alignItems} py="space1" pl="space2">
          {titleMeta && (
            <Label mb="space4">
              <MarkdownText
                disableLineBreaks
                source={titleMeta}
                allowHyperlinks
              />
            </Label>
          )}
          <FlexibleText
            as="h1"
            data-testid="resized-title"
            fontWeight="semiBold"
            color="text"
            fontFamily="heading"
            fontOffset={offset}
            fontSize={13}
            lineHeight={lineHeight}
            textAlign={textAlign}
          >
            {questionStyled && (!isEmptyObject(questionStyled) || !question) ? (
              <TextMarkupRenderer
                content={questionStyled}
                placeholder={
                  InteractiveCanvasComponents?.Title ? placeholder : undefined
                }
              />
            ) : (
              <MarkdownText
                disableLineBreaks
                allowHyperlinks
                source={question}
              />
            )}
          </FlexibleText>
          {questionDescription && (
            <Description
              shouldShow={shouldShowDescription}
              textAlign={textAlign}
            >
              <MarkdownText
                disableLineBreaks={false}
                source={questionDescription}
                allowHyperlinks
              />
            </Description>
          )}
        </Box>
      </StaggerCss>
    </Box>
  );
}
